/**
 * AttendanceScannerPage — opens the device camera and decodes student QR
 * badges in real time. Each successful decode posts to /api/attendance/scan-qr
 * which verifies the HMAC signature + marks the student "present" for today.
 *
 * Uses html5-qrcode (Google's library) which works on iOS Safari, Android
 * Chrome, and desktop browsers with a webcam.
 */
import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { useBranch } from "@/contexts/BranchContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ScanLine, Camera, CameraOff, CheckCircle2, AlertTriangle, RefreshCcw, BookOpen,
} from "lucide-react";

export default function AttendanceScannerPage() {
  const { selectedBranch } = useBranch();
  const [active, setActive] = useState(false);
  const [scanner, setScanner] = useState(null);
  const [recent, setRecent] = useState([]); // {student_id, name, ts, already_marked, error?}
  const [error, setError] = useState("");
  const [lastDecodedAt, setLastDecodedAt] = useState(0);
  const containerRef = useRef(null);
  const branchIdRef = useRef(selectedBranch?.id);

  useEffect(() => { branchIdRef.current = selectedBranch?.id; }, [selectedBranch?.id]);

  useEffect(() => {
    return () => {
      // best-effort cleanup on unmount
      if (scanner) {
        scanner.stop().catch(() => {}).finally(() => scanner.clear?.());
      }
    };
  }, [scanner]);

  async function start() {
    if (!selectedBranch) { toast.error("Pick a branch first"); return; }
    setError("");
    try {
      const h = new Html5Qrcode("qr-scanner-region");
      await h.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        onDecoded,
        () => { /* silently ignore per-frame decode failures */ },
      );
      setScanner(h);
      setActive(true);
    } catch (e) {
      setError(e?.message || String(e) || "Could not start camera");
      toast.error(e?.message || "Could not start camera. Grant camera permission and try again.");
    }
  }

  async function stop() {
    if (!scanner) { setActive(false); return; }
    try { await scanner.stop(); scanner.clear?.(); } catch { /* ignore */ }
    setScanner(null);
    setActive(false);
  }

  async function onDecoded(decodedText) {
    const now = Date.now();
    // Throttle: ignore the same code if scanned within 2s.
    if (now - lastDecodedAt < 2000) return;
    setLastDecodedAt(now);

    const branchId = branchIdRef.current;
    if (!branchId) return;
    try {
      const { data } = await api.post("/attendance/scan-qr", {
        payload: decodedText,
        branch_id: branchId,
      });
      const entry = {
        ts: new Date().toLocaleTimeString(),
        student_id: data.student_id,
        name: data.name,
        photo: data.photo,
        already_marked: data.already_marked,
        date: data.date,
      };
      setRecent((arr) => [entry, ...arr].slice(0, 30));
      if (data.already_marked) {
        toast(`${data.name} was already marked present`, { icon: "ℹ️" });
      } else {
        toast.success(`${data.name} — marked present`);
      }
    } catch (err) {
      const msg = formatApiError(err);
      setRecent((arr) => [{ ts: new Date().toLocaleTimeString(), error: msg }, ...arr].slice(0, 30));
      toast.error(msg);
    }
  }

  return (
    <div className="space-y-6" data-testid="attendance-scanner-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground tracking-tight">QR Attendance Scanner</h1>
          <p className="text-slate-500 mt-1">
            Point the camera at a student&apos;s ID badge. Marks today&apos;s attendance as <span className="font-semibold text-emerald-700">present</span> instantly.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {active ? (
            <Button onClick={stop} variant="outline" className="rounded-full" data-testid="scanner-stop-btn">
              <CameraOff className="w-4 h-4 mr-1.5" /> Stop camera
            </Button>
          ) : (
            <Button
              onClick={start}
              className="rounded-full bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] text-white shadow-floating"
              data-testid="scanner-start-btn"
            >
              <Camera className="w-4 h-4 mr-1.5" /> Start scanning
            </Button>
          )}
        </div>
      </div>

      {!selectedBranch && (
        <div className="bg-white rounded-3xl border border-amber-200 shadow-floating p-6 flex items-start gap-3 text-sm" data-testid="scanner-no-branch">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-foreground">Pick a branch first.</p>
            <p className="text-slate-500 mt-1">Scans need to know which branch&apos;s section to mark. Pick it in the top bar.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Camera viewport */}
        <div className="lg:col-span-3">
          <div className="bg-black rounded-3xl shadow-floating overflow-hidden relative aspect-square sm:aspect-video">
            <div id="qr-scanner-region" ref={containerRef} className="w-full h-full" data-testid="qr-scanner-region" />
            {!active && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white/80 pointer-events-none">
                <ScanLine className="w-16 h-16 opacity-50" />
                <p className="text-sm mt-2">Press &quot;Start scanning&quot; and grant camera permission.</p>
              </div>
            )}
          </div>
          {error && (
            <div className="rounded-2xl bg-[#FEE2E2] border border-red-200 p-3 text-sm text-[#991B1B] mt-3 flex items-start gap-2" data-testid="scanner-error">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}
        </div>

        {/* Recent scans rail */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-3xl border border-[#2563EB]/10 shadow-floating p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-foreground tracking-tight inline-flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-[#2563EB]" /> Recent scans
              </h3>
              {recent.length > 0 && (
                <button
                  onClick={() => setRecent([])}
                  className="text-xs text-slate-500 hover:text-[#1D4ED8] inline-flex items-center gap-1"
                  data-testid="scanner-clear-btn"
                >
                  <RefreshCcw className="w-3 h-3" /> Clear
                </button>
              )}
            </div>

            {recent.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">Scans appear here as they happen.</p>
            ) : (
              <ul className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                {recent.map((r, i) => (
                  <li
                    key={`${r.ts}-${i}`}
                    className={`rounded-2xl px-3 py-2 flex items-center gap-3 ${r.error ? "bg-[#FEE2E2]" : r.already_marked ? "bg-[#FEF3C7]" : "bg-[#DCFCE7]"}`}
                    data-testid={`scan-entry-${i}`}
                  >
                    {r.photo ? (
                      <img src={r.photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${r.error ? "bg-red-100 text-red-600" : "bg-white"}`}>
                        {r.error ? <AlertTriangle className="w-5 h-5" /> : <CheckCircle2 className={`w-5 h-5 ${r.already_marked ? "text-amber-600" : "text-emerald-600"}`} />}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      {r.error ? (
                        <>
                          <div className="text-sm font-semibold text-[#991B1B]">Scan failed</div>
                          <div className="text-xs text-[#991B1B] truncate">{r.error}</div>
                        </>
                      ) : (
                        <>
                          <div className="text-sm font-semibold text-foreground truncate">{r.name}</div>
                          <div className="text-xs text-slate-600">
                            {r.already_marked ? "Already marked present" : "Marked present"} · {r.ts}
                          </div>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
